// Reconcile a signed-in student (their own device) to their EXISTING roster entry,
// instead of creating a duplicate `students/{authUid}` doc. This is what makes the
// student-facing app "line up" with the teacher's roster, attendance, emergency, and
// sign-out board — the kid acts as their real roster identity.
//
// Resolution order:
//   1. VERIFIED school email (managed Google Workspace) → `students where schoolEmail == myEmail`.
//      This is the smooth, secure primary path (Google verifies the email; the rules
//      authorize exactly this self-lookup).
//   2. A previously CLAIMED doc (the join-code / name-pick fallback) → `students where claimedByUid == myUid`.
//
// On resolve we also "activate" the account: add the auth uid to each enrolled class's
// `enrolledStudents` (so the rules let the student read those class docs + their
// sign-out board) and stamp `claimedByUid` so later logins + downstream gates work.

import { useState, useEffect, useCallback } from "react";
import {
  collection, query, where, getDocs, limit,
  doc, getDoc, setDoc, updateDoc, arrayUnion, serverTimestamp,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { firestore } from "./FirebaseConfig";

// Join a class with a teacher's share code (Google Classroom style). The signed-in
// student is recognized by their Google identity; the code is the gate. Throws
// "invalid-code" if the code doesn't resolve. After this, useMyRosterStudent()
// resolves them (by email / claim) and they get the student app.
export async function joinClassWithCode(rawCode, user) {
  const code = (rawCode || "").trim().toUpperCase();
  if (!code) throw new Error("empty-code");

  const jc = await getDoc(doc(firestore, "joinCodes", code));
  if (!jc.exists()) throw new Error("invalid-code");
  const { classId, schoolId } = jc.data();
  if (!classId || !schoolId) throw new Error("invalid-code");

  const email = (user.email || "").trim().toLowerCase();

  // 1. Self-enroll into the class (grants read access; also satisfies the student
  //    self-create rule below). Allowed by the existing enrolledStudents-only rule.
  await updateDoc(doc(firestore, "classes", classId), {
    enrolledStudents: arrayUnion(user.uid),
  });

  // 2. If the teacher already rostered this kid (a doc with their email), reuse it —
  //    just claim it, so we never create a duplicate. Otherwise create their own doc
  //    from their Google profile.
  let existing = null;
  if (email) {
    const snap = await getDocs(query(
      collection(firestore, "students"),
      where("schoolEmail", "==", email),
      limit(1)
    ));
    if (!snap.empty) existing = { id: snap.docs[0].id, ...snap.docs[0].data() };
  }

  if (existing && existing.id !== user.uid) {
    await updateDoc(doc(firestore, "students", existing.id), {
      claimedByUid: user.uid,
      studentEmail: email,
      claimedAt: serverTimestamp(),
    });
  } else {
    const [firstName, ...rest] = (user.displayName || "").split(" ");
    await setDoc(doc(firestore, "students", user.uid), {
      uid: user.uid,
      studentName: user.displayName || email || "Student",
      firstName: firstName || "",
      lastName: rest.join(" "),
      schoolEmail: email,
      studentEmail: email,
      schoolId,
      enrolledClasses: arrayUnion(classId),
      homeroomClassId: classId,
      claimedByUid: user.uid,
      addedBy: "self",
    }, { merge: true });
  }
}

async function findRosterStudent(user) {
  const email = (user.email || "").trim().toLowerCase();

  if (email) {
    const byEmail = await getDocs(query(
      collection(firestore, "students"),
      where("schoolEmail", "==", email),
      limit(1)
    ));
    if (!byEmail.empty) return { id: byEmail.docs[0].id, ...byEmail.docs[0].data() };
  }

  const byClaim = await getDocs(query(
    collection(firestore, "students"),
    where("claimedByUid", "==", user.uid),
    limit(1)
  ));
  if (!byClaim.empty) return { id: byClaim.docs[0].id, ...byClaim.docs[0].data() };

  return null;
}

// Grant class-read access + persist the claim link. Best-effort: the app still works
// off the resolved doc even if these writes are denied (e.g. a stale rule).
async function activate(student, user) {
  const classIds = student.enrolledClasses || [];
  await Promise.all(classIds.map((cid) =>
    updateDoc(doc(firestore, "classes", cid), { enrolledStudents: arrayUnion(user.uid) })
      .catch(() => {})
  ));

  if (student.claimedByUid !== user.uid) {
    await updateDoc(doc(firestore, "students", student.id), {
      claimedByUid: user.uid,
      studentEmail: (user.email || "").trim().toLowerCase(),
      claimedAt: serverTimestamp(),
    }).catch(() => {});
  }
}

// { loading, user, student, error, refresh }
export function useMyRosterStudent() {
  const [state, setState] = useState({ loading: true, user: null, student: null, error: null });

  const resolve = useCallback(async (user) => {
    if (!user) {
      setState({ loading: false, user: null, student: null, error: null });
      return;
    }
    try {
      const student = await findRosterStudent(user);
      if (student) await activate(student, user);
      setState({ loading: false, user, student, error: null });
    } catch (e) {
      console.error("Student identity resolve failed:", e);
      setState({ loading: false, user, student: null, error: e });
    }
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (user) => {
      setState((s) => ({ ...s, loading: true }));
      resolve(user);
    });
    return () => unsub();
  }, [resolve]);

  const refresh = useCallback(() => {
    const user = getAuth().currentUser;
    setState((s) => ({ ...s, loading: true }));
    resolve(user);
  }, [resolve]);

  return { ...state, refresh };
}
